import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import {
  IReportActor,
  IReportTasks,
  IReportTasksDetailedTaskStats,
  TaskStatusCounts
} from '@dadohr/shared/domainTypes/reports';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import { ButtonBase, Tooltip, Typography } from '@material-ui/core';
import { capitalize, sortBy } from 'lodash';
import { useMemo, useState } from 'react';
import { WithShape } from '../../../../components/Charts/Util';
import { formatNumber } from '../../../../components/Number';
import { TooltipWhite } from '../../../../components/Tooltip';
import { useHoverDurationTracking } from '../../../../hooks/useHoverDurationTracking';
import { useUnsafeTranslation } from '../../../../i18next';
import { Centered } from '../../../../layout/Centered';
import { FlexContainer } from '../../../../layout/Flex';
import { ONE_DAY } from '../../../../services/time';
import { useTheme } from '../../../../themes';
import {
  getItemDisplayName,
  getItemDisplayPair,
  resolveExpTitle
} from '../../../Experiences/helpers/adminName';
import { ActorSelector } from '../ActorSelector';
import { ChartTitle } from '../ChartTitle';

const COLORS = {
  onTime: (colors: Theme['custom']['colors']) => colors.green.w400,
  late: (colors: Theme['custom']['colors']) => colors.green.w500
};

const Container = styled('div')((p) => ({
  marginTop: p.theme.spacing(3)
}));

const getRelevantTotal = (x: TaskStatusCounts) => {
  return x.completedLate + x.completedOnTime + x.overdue;
};

const getCompletionPercentage = (x: TaskStatusCounts) => {
  const total = getRelevantTotal(x);
  if (total === 0) {
    return 0;
  }
  const completed = x.completedLate + x.completedOnTime;
  return completed / total;
};

const getActorWithDefaults = (
  actors: IReportActor[],
  actorKey: string
): IReportActor => {
  const actor = actors.find((a) => a.key === actorKey);
  return {
    key: actorKey,
    label: actorKey,
    color: '#000',
    ...actor
  };
};

const TaskGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(200px, 1fr) 4fr',
  gridColumnGap: p.theme.spacing(2),
  gridRowGap: p.theme.spacing(2),
  alignItems: 'center'
}));

const BarContainer = styled('div')((p) => ({
  width: '100%',
  display: 'flex'
}));

const BarContainerInner = styled('div')<{
  percentage: number; // 0-1
}>((p) => ({
  // Give some space on the right so that the rendered percentage fits better
  width: `calc(${Math.round(p.percentage * 100)}% - ${p.theme.spacing(5)}px)`,
  marginRight: p.theme.spacing(1),
  display: 'flex'
}));

const BarSegment = styled('div')<{
  percentage: number; // 0-1
  color: string;
}>((p) => ({
  height: p.theme.spacing(3),
  width: `${Math.round(p.percentage * 100)}%`,
  background: p.color
}));

const CompletionPercentage = styled.span`
  color: ${(p) => p.theme.custom.colors.gray.w500};
`;

const CompletionBar = ({
  total,
  late,
  onTime
}: {
  total: number;
  late: number;
  onTime: number;
}) => {
  const { t } = useUnsafeTranslation();
  const {
    custom: { colors }
  } = useTheme();
  const percentage = (late + onTime) / total;
  const latePercent = late / total;
  const relLatePercent = late / (late + onTime);
  const onTimePercent = onTime / total;
  const relOnTimePercent = onTime / (late + onTime);
  return (
    <BarContainer>
      {!!percentage && (
        <TooltipWhite
          title={
            <div>
              <FlexContainer spacing={2} justifyContent="space-between">
                <WithShape color="lightgray" shape="square" narrow>
                  {capitalize(t('total'))}{' '}
                </WithShape>
                <strong>
                  {formatNumber({
                    n: percentage,
                    format: 'percent'
                  })}
                </strong>
              </FlexContainer>
              <FlexContainer spacing={2} justifyContent="space-between">
                <WithShape color={COLORS.onTime(colors)} shape="square" narrow>
                  {capitalize(t('done on time'))}{' '}
                </WithShape>
                <strong>
                  {formatNumber({
                    n: onTimePercent,
                    format: 'percent'
                  })}
                </strong>
              </FlexContainer>
              <FlexContainer spacing={2} justifyContent="space-between">
                <WithShape color={COLORS.late(colors)} shape="square" narrow>
                  {capitalize(t('done late'))}{' '}
                </WithShape>
                <strong>
                  {formatNumber({
                    n: latePercent,
                    format: 'percent'
                  })}
                </strong>
              </FlexContainer>
            </div>
          }
        >
          <BarContainerInner percentage={percentage}>
            <BarSegment
              percentage={relOnTimePercent}
              color={COLORS.onTime(colors)}
            />
            <BarSegment
              percentage={relLatePercent}
              color={COLORS.late(colors)}
            />
          </BarContainerInner>
        </TooltipWhite>
      )}
      <CompletionPercentage>
        {formatNumber({
          n: percentage,
          format: 'percent'
        })}
      </CompletionPercentage>
    </BarContainer>
  );
};

const TaskRow = ({
  task,
  actors,
  experienceTitle,
  tpl
}: {
  task: IReportTasksDetailedTaskStats;
  actors: IReportActor[];
  experienceTitle: string;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  const actor = getActorWithDefaults(actors, task.actorKey);
  const tplTask = tpl.data.tasks.find((t) => t.id === task.taskTemplateId);
  const resolvedTask = {
    ...task,
    title: resolveExpTitle(task.title, experienceTitle),
    adminName: tplTask?.adminName != null
      ? resolveExpTitle(tplTask.adminName, experienceTitle)
      : tplTask?.adminName
  };
  return (
    <>
      <Tooltip
        title={
          <div>
            <div>
              <WithShape color={actor.color}>
                <strong>{actor.label}</strong>
              </WithShape>
            </div>
            <div>
              {(() => {
                const { primary, secondary } = getItemDisplayPair(resolvedTask);
                return (
                  <>
                    <strong>{primary}</strong>
                    {secondary && <div><em>"{secondary}"</em></div>}
                  </>
                );
              })()}
            </div>
            <Typography variant="caption">
              {t('Starts on day {{ day }}', {
                day: Math.round(task.regularStart / ONE_DAY) + 1
              })}
            </Typography>
          </div>
        }
      >
        <span>
          <WithShape color={actor.color} ellipsis>
            {getItemDisplayName(resolvedTask)}
          </WithShape>
        </span>
      </Tooltip>
      {task.hasAnyPastDeadline ? (
        <CompletionBar
          total={getRelevantTotal(task.counts)}
          late={task.counts.completedLate}
          onTime={task.counts.completedOnTime}
        />
      ) : (
        <div>
          <TooltipWhite
            title={`${t(
              'None of the experiences in your reporting time frame have reached the completion deadline for this task yet.'
            )}`}
          >
            <CompletionPercentage>
              {t('Not enough data yet')}
            </CompletionPercentage>
          </TooltipWhite>
        </div>
      )}
    </>
  );
};

const TaskGridToolbar = ({
  dir,
  setDir
}: {
  dir: 'asc' | 'desc';
  setDir: (nextDir: 'asc' | 'desc') => void;
}) => {
  const { t } = useUnsafeTranslation();
  const {
    custom: { colors }
  } = useTheme();
  return (
    <>
      <div />
      <Typography variant="caption">
        <FlexContainer justifyContent="space-between">
          <ButtonBase onClick={() => setDir(dir === 'asc' ? 'desc' : 'asc')}>
            <strong>
              <FlexContainer>
                <div>{t('Completion rate')}</div>
                <div>{dir === 'asc' ? '▲' : '▼'}</div>
              </FlexContainer>
            </strong>
          </ButtonBase>

          <FlexContainer spacing={2}>
            <WithShape shape="square" narrow color={COLORS.onTime(colors)}>
              {capitalize(t('done on time'))}
            </WithShape>
            <WithShape shape="square" narrow color={COLORS.late(colors)}>
              {capitalize(t('done late'))}
            </WithShape>
          </FlexContainer>
        </FlexContainer>
      </Typography>
    </>
  );
};

export const CompletionPerTask = ({
  actors,
  data,
  experienceTitle,
  tpl
}: {
  actors: IReportActor[];
  data: IReportTasks['data']['byTask'];
  experienceTitle: string;
  tpl: Doc<IExperienceTemplate>;
}) => {
  const { t } = useUnsafeTranslation();
  const hoverTrackingProps = useHoverDurationTracking({
    name: 'Completion Per Tasks Chart Hovered',
    explanation:
      'Admin user hovered over the bar chart showing completion rate per task'
  });
  const [state, setState] = useState<{
    actors: string[];
    sortDir: 'asc' | 'desc';
  }>({ actors: actors.map((a) => a.key), sortDir: 'desc' });
  const sortedTasks = useMemo(() => {
    return sortBy(
      data.filter((d) => state.actors.includes(d.actorKey)),
      (d) => {
        if (d.hasAnyPastDeadline) {
          return (
            getCompletionPercentage(d.counts) *
            (state.sortDir === 'asc' ? 1 : -1)
          );
        } else {
          return state.sortDir === 'asc' ? -Infinity : Infinity;
        }
      }
    );
  }, [data, state]);

  return (
    <>
      <ChartTitle
        title={t('Completion per task')}
        tooltipText={`${t(
          'The rate at which tasks were completed, excluding Upcoming and Skipped tasks'
        )}`}
      />
      <Container {...hoverTrackingProps}>
        <ActorSelector
          actors={actors}
          selected={state.actors}
          setSelected={(nextActors) =>
            setState((s) => ({ ...s, actors: nextActors }))
          }
          marginBottom={4}
          trackingKey="Completion per tasks"
        />
        {sortedTasks.length > 0 ? (
          <TaskGrid>
            <TaskGridToolbar
              dir={state.sortDir}
              setDir={(nextDir) =>
                setState((s) => ({ ...s, sortDir: nextDir }))
              }
            />
            {sortedTasks.map((task) => (
              <TaskRow key={task.taskTemplateId} task={task} actors={actors} experienceTitle={experienceTitle} tpl={tpl} />
            ))}
          </TaskGrid>
        ) : (
          <Centered height={200}>
            <Typography>{t('No tasks match these criteria.')}</Typography>
          </Centered>
        )}
      </Container>
    </>
  );
};
